import { IRecordsState, ViewMode } from './types';
import initialState from '../store/initialState';
import {
  SET_INITIAL_LOAD_IN_PROGRESS,
  SET_LATEST_PUBLIC_RECORDS_WITH_SUMMARIES,
  ADD_LATEST_PUBLIC_RECORDS,
  UPDATE_LATEST_PUBLIC_RECORD,
  REMOVE_LATEST_PUBLIC_RECORD,
  RESET_RECORDS,
  ADD_RECORD,
  APPEND_RECORDS,
  SET_CURRENT_RECORD,
  SET_RECORD_CONFIRMED_BY_BACKEND,
  SET_RECORD_STATE,
  DELETE_RECORD,
  UNLINK_RECORD_TENANT,
  SET_VIEW_MODE,
  TOGGLE_EXPAND_CONDENSED_VIEW_MODE,
  SET_SHOW_SUMMARIES,
  SET_SHOW_ALL_LINKABLE,
  SET_SEARCH_TEXT,
  SET_SEARCH_TYPE,
  SET_INCLUDE_ARCHIVED,
  SET_SELECTED_SUMMARIES_RECORD_ID,
  SET_RECORD_AS_NON_LINKABLE,
  TOGGLE_RECORD_MAXIMIZE,
  UPDATE_RECORD_FIELD,
  SET_RECORD_FORM_ERRORS,
  RESET_RECORD_FORM,
  RecordActionTypes,
} from './types';
import {
  ADD_SUBRECORD,
  DELETE_SUBRECORD,
  IAddSubrecordAction,
  IDeleteSubrecordAction,
  SET_SUBRECORD_CONFIRMED_BY_BACKEND,
  ISetSubrecordConfirmedByBackendAction,
} from '../subrecords/types';
import { SET_SAAS_TENANT_CONFIRMED_BY_BACKEND, ISetSaasTenantConfirmedByBackendAction } from '../saasTenants/types';

// Reducer function for managing record state
export const recordsReducer = (
  state: IRecordsState = initialState.records,
  action: RecordActionTypes | IAddSubrecordAction | IDeleteSubrecordAction | ISetSubrecordConfirmedByBackendAction | ISetSaasTenantConfirmedByBackendAction,
): IRecordsState => {
  switch (action.type) {
    case SET_INITIAL_LOAD_IN_PROGRESS:
      // Track whether initial records load is in progress
      return {
        ...state,
        isInitialLoadInProgress: action.payload,
      };

    case RESET_RECORDS:
      // Reset the list of records
      return {
        ...state,
        records: [],
        currentRecordId: null,
        processingStartTimes: {},
        maximizedRecords: {},
      };

    case ADD_RECORD:
      // Add new record to the beginning of the list
      const record = action.payload;
      return {
        ...state,
        records: [{ ...record, subrecordsCount: 0, updated_at: new Date().toISOString() }, ...state.records],
        currentRecordId: record.record_id,
        // Start processing timer if record is onroute
        processingStartTimes: record.onroute
          ? {
              ...state.processingStartTimes,
              [record.record_id]: Date.now(),
            }
          : state.processingStartTimes,
      };

    case APPEND_RECORDS:
      // Replace the list of records
      return {
        ...state,
        records: [...state.records, ...action.payload],
        currentRecordId: state.records.length === 0 && action.payload.length > 0 ? action.payload[0].record_id : null,
        viewMode: action.payload.length === 0 ? ViewMode.SHOW_FORM : state.searchText ? ViewMode.SHOW_SEARCH : state.viewMode,
      };

    case SET_CURRENT_RECORD:
      // Update the current record ID
      return {
        ...state,
        currentRecordId: action.payload,
      };

    case SET_RECORD_CONFIRMED_BY_BACKEND:
      // Set record as no longer on route and stop timer
      const { [action.record_id]: removedRecordTimer, ...remainingRecordTimers } = state.processingStartTimes;
      return {
        ...state,
        records: state.records.map((rec) =>
          rec.record_id === action.record_id ? { ...rec, ...action.data, onroute: !!action.data.interim, updated_at: new Date().toISOString() } : rec,
        ),
        processingStartTimes: action.data.interim ? state.processingStartTimes : remainingRecordTimers,
      };

    case SET_RECORD_STATE:
      // Find the record to update
      const updatedRecord = state.records.find((rec) => rec.record_id === action.payload.record_id);
      if (!updatedRecord) {
        console.warn('Record not found when updating state', action.payload);
        return state;
      }

      // Only reorder if updated_at is present, otherwise just update in place
      if (action.payload.updated_at) {
        return {
          ...state,
          records: [
            // Put updated record first to maintain descending updated_at order
            { ...updatedRecord, ...action.payload },
            ...state.records.filter((rec) => rec.record_id !== action.payload.record_id),
          ],
        };
      } else {
        return {
          ...state,
          records: state.records.map((rec) => (rec.record_id === action.payload.record_id ? { ...rec, ...action.payload } : rec)),
        };
      }

    case DELETE_RECORD:
      // Remove record from the list and update currentRecordId (prefer preceding record when deleting the current record)
      const deletedRecordId = action.payload;
      const deletedRecordIndex = state.records.findIndex((rec) => rec.record_id === deletedRecordId);
      const records = state.records.filter((rec) => rec.record_id !== deletedRecordId);

      const isDeletingCurrentRecord = state.currentRecordId === deletedRecordId;
      const precedingRecordId = deletedRecordIndex > 0 ? state.records[deletedRecordIndex - 1]?.record_id : null;
      const nextCurrentRecordId = isDeletingCurrentRecord ? precedingRecordId || records[0]?.record_id || null : state.currentRecordId;
      return {
        ...state,
        records,
        currentRecordId: nextCurrentRecordId,
      };

    // When the backend sends dataDeleted.records with an `unlinkedBy` tenant id, the record is not deleted globally.
    // Instead, it is only unshared from that tenant, so we remove that tenant id from the record's shared tenant lists.
    case UNLINK_RECORD_TENANT: {
      const { record_id, unlinkedBy } = action.payload;

      const recordIndex = state.records.findIndex((rec) => rec.record_id === record_id);
      if (recordIndex < 0) return state;

      const recordToUpdate = state.records[recordIndex];
      const updatedRecord = {
        ...recordToUpdate,
        sharedSaasTenantIdsAndNames: (recordToUpdate.sharedSaasTenantIdsAndNames || []).filter((t) => t.id !== unlinkedBy),
      };

      return {
        ...state,
        records: [...state.records.slice(0, recordIndex), updatedRecord, ...state.records.slice(recordIndex + 1)],
      };
    }

    case SET_VIEW_MODE:
      return {
        ...state,
        viewMode: action.payload,
      };

    case TOGGLE_EXPAND_CONDENSED_VIEW_MODE:
      return {
        ...state,
        viewMode: state.viewMode === ViewMode.SHOW_CONDENSED_RECORDS_VIEW ? ViewMode.SHOW_EXPANDED_VIEW : ViewMode.SHOW_CONDENSED_RECORDS_VIEW,
        maximizedRecords: {},
      };

    case SET_SHOW_SUMMARIES:
      return {
        ...state,
        showSummaries: action.payload,
      };

    case SET_SHOW_ALL_LINKABLE:
      return {
        ...state,
        showAllLinkable: action.payload,
      };

    case SET_SEARCH_TEXT:
      return {
        ...state,
        searchText: action.payload,
      };

    case SET_SEARCH_TYPE:
      return {
        ...state,
        searchType: action.payload,
      };

    case SET_INCLUDE_ARCHIVED:
      return {
        ...state,
        includeArchived: action.payload,
      };

    case SET_LATEST_PUBLIC_RECORDS_WITH_SUMMARIES:
      return {
        ...state,
        latestPublicRecords: action.payload,
      };

    case ADD_LATEST_PUBLIC_RECORDS:
      // Add new public record to the beginning of the list, avoiding duplicates
      const existingRecordIndex = state.latestPublicRecords.findIndex((r) => r.record_id === action.payload.record_id);
      if (existingRecordIndex >= 0) {
        // Update existing record
        return {
          ...state,
          latestPublicRecords: [action.payload, ...state.latestPublicRecords.filter((_, index) => index !== existingRecordIndex)],
        };
      } else {
        // Add new record to the beginning
        return {
          ...state,
          latestPublicRecords: [action.payload, ...state.latestPublicRecords],
        };
      }

    case UPDATE_LATEST_PUBLIC_RECORD:
      // Update record from latest public records
      return {
        ...state,
        latestPublicRecords: state.latestPublicRecords.map((rec) =>
          rec.record_id !== action.payload ? rec : { ...rec, updated_at: new Date().toISOString() },
        ),
      };

    case REMOVE_LATEST_PUBLIC_RECORD:
      // Remove record from latest public records
      return {
        ...state,
        latestPublicRecords: state.latestPublicRecords.filter((rec) => rec.record_id !== action.payload),
      };

    case SET_SELECTED_SUMMARIES_RECORD_ID:
      return {
        ...state,
        selectedSummariesRecordId: action.payload,
      };

    case SET_RECORD_AS_NON_LINKABLE:
      return {
        ...state,
        latestPublicRecords: state.latestPublicRecords.map((rec) => (rec.record_id === action.payload ? { ...rec, isLinkable: false } : rec)),
      };

    case TOGGLE_RECORD_MAXIMIZE:
      return {
        ...state,
        maximizedRecords: {
          ...state.maximizedRecords,
          [action.payload]: !state.maximizedRecords[action.payload],
        },
      };

    case UPDATE_RECORD_FIELD:
      // Update record form field
      return {
        ...state,
        recordForm: {
          ...state.recordForm,
          [action.payload.field]: action.payload.value,
          errors: {
            ...state.recordForm.errors,
            [action.payload.field]: '',
          },
        },
      };

    case SET_RECORD_FORM_ERRORS:
      // Update record form errors
      return {
        ...state,
        recordForm: {
          ...state.recordForm,
          errors: action.payload,
        },
      };

    case RESET_RECORD_FORM:
      // Reset record form to initial state
      return {
        ...state,
        recordForm: initialState.records.recordForm,
      };

    case ADD_SUBRECORD:
      // Increment subrecord count when a subrecord is added
      const subrecord = (action as IAddSubrecordAction).payload;
      return {
        ...state,
        records: state.records.map((record) =>
          record.record_id === subrecord.record_id
            ? { ...record, subrecordsCount: (record.subrecordsCount || 0) + 1, updated_at: new Date().toISOString() }
            : record,
        ),
        // Start processing timer if subrecord is onroute and not non_ai
        processingStartTimes:
          (subrecord.onroute || !subrecord.ai_text) && !subrecord.non_ai
            ? {
                ...state.processingStartTimes,
                [subrecord.subrecord_id]: Date.now(),
              }
            : state.processingStartTimes,
      };

    case DELETE_SUBRECORD:
      // Decrement subrecord count when a subrecord is deleted
      return {
        ...state,
        records: state.records.map((record) =>
          record.record_id === (action as IDeleteSubrecordAction).payload.recordId
            ? { ...record, subrecordsCount: Math.max((record.subrecordsCount || 0) - 1, 0), updated_at: new Date().toISOString() }
            : record,
        ),
      };

    case SET_SUBRECORD_CONFIRMED_BY_BACKEND:
      if (action.data.ai_text) {
        // Stop timer when subrecord processing completes with AI text
        const { [action.subrecordId]: removedSubrecordTimer, ...remainingSubrecordTimers } = state.processingStartTimes;
        return {
          ...state,
          processingStartTimes: action.data.interim ? state.processingStartTimes : remainingSubrecordTimers,
        };
      }
      return state;

    case SET_SAAS_TENANT_CONFIRMED_BY_BACKEND:
      // Update saas_tenant_name for all records with matching saas_tenant_id
      return {
        ...state,
        records: state.records.map((record) =>
          record.saas_tenant_id === action.saas_tenant_id && action.saas_tenant_name ? { ...record, saas_tenant_name: action.saas_tenant_name } : record,
        ),
      };

    default:
      // Return state unchanged for unknown actions
      return state;
  }
};
